import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../../AppContext';

const VisionCreator = ({ onClose, sharedContent }) => {
    const { t } = useTranslation();
    const { appFunctions, showConfirmation, userId, uploadFile } = useAppContext();
    
    // If sharedContent exists, we derive the type and preview from it.
    // Otherwise, the user will select a type.
    const [visionType, setVisionType] = useState(sharedContent ? 'sharedMoment' : null);
    const [text, setText] = useState(''); // This will now serve as the caption for shared content
    const [backgroundColor, setBackgroundColor] = useState('#000000');
    const [file, setFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAiModal, setShowAiModal] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [duration, setDuration] = useState(7000); // Default 7 seconds
    const [textOverlays, setTextOverlays] = useState([]);
    const [activeOverlayIndex, setActiveOverlayIndex] = useState(null);
    const [isEditingText, setIsEditingText] = useState(false);
    const [stickers, setStickers] = useState([]);

    // State for Music Integration
    const [showMusicModal, setShowMusicModal] = useState(false);
    const [musicTracks] = useState([
        { id: 'track1', title: 'Uplifting Dreams', artist: 'Harmony AI' },
        { id: 'track2', title: 'Cosmic Flow', artist: 'Starlight Beats' },
        { id: 'track3', title: 'Neon Nights', artist: 'Synthwave Explorer' },
        { id: 'track4', title: 'Peaceful Journey', artist: 'Aura Soundscapes' },
    ]);
    const [selectedMusic, setSelectedMusic] = useState(null);
    const [originalAudioVolume, setOriginalAudioVolume] = useState(100);
    const [musicVolume, setMusicVolume] = useState(100);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setVisionType(selectedFile.type.startsWith('image') ? 'image' : 'video');
        }
    };

    const handleGenerateContent = async () => {
        const generateVisionContent = httpsCallable(appFunctions, 'generateVisionContent');
        try {
            const result = await generateVisionContent({ prompt });
            setText(result.data.generatedText);
            setVisionType('text');
            setShowAiModal(false);
        } catch (error) {
            console.error("Error generating vision content:", error);
        }
    };

    const addTextOverlay = () => {
        const newOverlay = {
            text: 'Your Text Here',
            color: '#FFFFFF',
            position: { x: 50, y: 50 },
        };
        setTextOverlays([...textOverlays, newOverlay]);
        setActiveOverlayIndex(textOverlays.length);
    };

    const updateOverlayText = (index, newText) => {
        const updatedOverlays = [...textOverlays];
        updatedOverlays[index].text = newText;
        setTextOverlays(updatedOverlays);
    };

    const updateOverlayPosition = (index, newPosition) => {
        const updatedOverlays = [...textOverlays];
        updatedOverlays[index].position = newPosition;
        setTextOverlays(updatedOverlays);
    };

    const addSticker = (type) => {
        const newSticker = {
            type,
            position: { x: 50, y: 50 },
            emoji: '❤️', // Default for react sticker
        };
        setStickers([...stickers, newSticker]);
    };

    const updateStickerPosition = (index, newPosition) => {
        const updatedStickers = [...stickers];
        updatedStickers[index].position = newPosition;
        setStickers(updatedStickers);
    };

    const createAndUploadVision = async (isPromoted) => {
        setIsSubmitting(true);
        const createVision = httpsCallable(appFunctions, 'createVision');

        try {
            let visionData = {
                duration,
                text,
                backgroundColor,
                isPromoted,
                textOverlays,
                stickers,
                music: {
                    trackId: selectedMusic,
                    volume: musicVolume,
                },
                audioSettings: {
                    originalVolume: originalAudioVolume,
                }
            };

            if (visionType === 'sharedMoment' && sharedContent) {
                visionData.sharedContent = { id: sharedContent.id, type: 'moment' };
                // CRITICAL FIX: DO NOT send contentURL or type directly.
                // Let the backend derive it from the sharedContent.
            } else if (file) {
                const filePath = `visions/${userId}/${Date.now()}_${file.name}`;
                const contentURL = await uploadFile(file, filePath, () => {});
                visionData.contentURL = contentURL;
                visionData.type = visionType;
            } else {
                 visionData.type = visionType;
            }

            const result = await createVision(visionData);

            if (isPromoted) {
                const promoteVision = httpsCallable(appFunctions, 'promoteVision');
                await promoteVision({ visionId: result.data.visionId });
            }

            onClose();
        } catch (error) {
            console.error("Error creating vision:", error);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleCreateVision = async (isPromoted = false) => {
        if (!visionType) {
            alert(t('visions.creator.selectTypeAlert'));
            return;
        }

        if (isPromoted) {
            showConfirmation({
                message: t('visions.creator.promoteConfirm', { cost: 5000 }),
                onConfirm: () => createAndUploadVision(true)
            });
        } else {
            await createAndUploadVision(false);
        }
    };

    const renderContent = () => {
        // NEW: Streamlined UI for sharing a Moment
        if (visionType === 'sharedMoment' && sharedContent) {
            return (
                <div className="relative w-full h-96 rounded-lg overflow-hidden flex items-center justify-center bg-black">
                    {/* Video Background */}
                    <video
                        src={sharedContent.mediaUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute top-0 left-0 w-full h-full object-cover z-0"
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/50 z-10"></div>

                    {/* Content */}
                    <div className="relative z-20 w-full p-4 flex flex-col items-center">
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder={t('visions.creator.captionPlaceholder')}
                            className="w-full bg-transparent border-none text-white text-2xl font-bold text-center focus:outline-none resize-none mb-4"
                            rows="3"
                        />
                        <div className="w-full max-w-xs">
                            <label className="text-white text-sm mr-2">{t('visions.creator.durationLabel')}: {duration / 1000}s</label>
                            <input
                                type="range"
                                min="3000"
                                max="15000"
                                step="1000"
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value))}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            );
        }

        // Original UI for creating from scratch
        if (!visionType) {
            return (
                <div className="flex justify-around">
                    <button onClick={() => setVisionType('text')} className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-700">
                        <LucideIcons.Type size={48} className="text-purple-300 mb-2" />
                        <span className="text-white">{t('visions.creator.textType')}</span>
                    </button>
                    <label className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-700 cursor-pointer">
                        <LucideIcons.Image size={48} className="text-purple-300 mb-2" />
                        <span className="text-white">{t('visions.creator.mediaType')}</span>
                        <input type="file" accept="image/*,video/*" onChange={handleFileChange} className="hidden" />
                    </label>
                    <button onClick={() => setShowAiModal(true)} className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-700">
                        <LucideIcons.Sparkles size={48} className="text-purple-300 mb-2" />
                        <span className="text-white">{t('visions.creator.aiType')}</span>
                    </button>
                </div>
            );
        }

        // Original creation forms
        return (
            <div>
                {visionType === 'text' && (
                    <div className="flex flex-col">
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder={t('visions.creator.textPlaceholder')}
                            className="w-full h-48 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white mb-4"
                            style={{ backgroundColor }}
                        />
                        <div className="flex items-center mb-4">
                            <label className="text-white mr-2">{t('visions.creator.bgColorLabel')}</label>
                            <input type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} />
                        </div>
                    </div>
                )}

                {(visionType === 'image' || visionType === 'video') && file && (
                    <div className="relative flex justify-center mb-4">
                        {visionType === 'image' && <img src={URL.createObjectURL(file)} alt={t('visions.creator.imagePreviewAlt')} className="max-h-64 rounded-lg" />}
                        {visionType === 'video' && <video src={URL.createObjectURL(file)} controls className="max-h-64 rounded-lg" />}
                        {textOverlays.map((overlay, index) => (
                            <motion.div
                                key={index}
                                drag
                                onDragEnd={(event, info) => updateOverlayPosition(index, { x: info.point.x, y: info.point.y })}
                                className="absolute"
                                style={{ top: overlay.position.y, left: overlay.position.x, color: overlay.color }}
                                onDoubleClick={() => {
                                    setActiveOverlayIndex(index);
                                    setIsEditingText(true);
                                }}
                            >
                                {isEditingText && activeOverlayIndex === index ? (
                                    <input
                                        type="text"
                                        value={overlay.text}
                                        onChange={(e) => updateOverlayText(index, e.target.value)}
                                        onBlur={() => setIsEditingText(false)}
                                        autoFocus
                                    />
                                ) : (
                                    <span>{overlay.text}</span>
                                )}
                            </motion.div>
                        ))}
                        {stickers.map((sticker, index) => (
                            <motion.div
                                key={index}
                                drag
                                onDragEnd={(event, info) => updateStickerPosition(index, { x: info.point.x, y: info.point.y })}
                                className="absolute text-4xl"
                                style={{ top: sticker.position.y, left: sticker.position.x }}
                            >
                                {sticker.emoji}
                            </motion.div>
                        ))}
                    </div>
                )}

                {(visionType === 'image' || visionType === 'video') && (
                    <div className="flex space-x-2">
                        <button onClick={addTextOverlay} className="flex items-center p-2 rounded-lg hover:bg-gray-700">
                            <LucideIcons.Type size={24} className="text-purple-300 mr-2" />
                            <span className="text-white">Add Text</span>
                        </button>
                        <button onClick={() => addSticker('react')} className="flex items-center p-2 rounded-lg hover:bg-gray-700">
                            <LucideIcons.Heart size={24} className="text-purple-300 mr-2" />
                            <span className="text-white">Add React</span>
                        </button>
                        <button onClick={() => setShowMusicModal(true)} className="flex items-center p-2 rounded-lg hover:bg-gray-700">
                            <LucideIcons.Music size={24} className="text-purple-300 mr-2" />
                            <span className="text-white">Music</span>
                        </button>
                    </div>
                )}

                <div className="mb-4">
                    <label className="text-white mr-2">{t('visions.creator.durationLabel')}: {duration / 1000}s</label>
                    <input
                        type="range"
                        min="3000"
                        max="15000"
                        step="1000"
                        value={duration}
                        onChange={(e) => setDuration(Number(e.target.value))}
                        className="w-full"
                    />
                </div>
            </div>
        );
    };

    const MusicModal = () => (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900/80 p-6 rounded-xl shadow-glow max-w-md w-full border border-purple-500/50">
                <h3 className="text-xl font-bold text-white mb-4">Add Music</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                    {musicTracks.map(track => (
                        <button 
                            key={track.id} 
                            onClick={() => setSelectedMusic(track.id)}
                            className={`w-full text-left p-2 rounded-lg ${selectedMusic === track.id ? 'bg-purple-600' : 'bg-gray-800 hover:bg-gray-700'}`}
                        >
                            <p className="font-semibold">{track.title}</p>
                            <p className="text-sm text-gray-400">{track.artist}</p>
                        </button>
                    ))}
                </div>
                {visionType === 'video' && (
                    <div className="mt-4">
                        <label className="text-white">Original Audio: {originalAudioVolume}%</label>
                        <input type="range" min="0" max="100" value={originalAudioVolume} onChange={(e) => setOriginalAudioVolume(e.target.value)} className="w-full" />
                    </div>
                )}
                <div className="mt-4">
                    <label className="text-white">Music Volume: {musicVolume}%</label>
                    <input type="range" min="0" max="100" value={musicVolume} onChange={(e) => setMusicVolume(e.target.value)} className="w-full" />
                </div>
                <div className="flex justify-end mt-6">
                    <button onClick={() => setShowMusicModal(false)} className="px-6 py-2 bg-purple-600 text-white font-bold rounded-full hover:bg-purple-700 transition-colors">
                        Done
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <motion.div
                className="bg-gray-900/80 p-6 rounded-xl shadow-glow max-w-lg w-full border border-purple-500/50"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-white font-playfair">{t('visions.creator.title')}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <LucideIcons.X size={24} />
                    </button>
                </div>

                {renderContent()}

                {visionType && (
                     <div className="flex justify-end items-center gap-4 mt-4">
                        <div className="text-right">
                            <motion.button 
                                onClick={() => handleCreateVision(true)} 
                                disabled={isSubmitting} 
                                className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold rounded-full shadow-lg flex items-center gap-2"
                                whileHover={{ scale: 1.05 }}
                            >
                                <LucideIcons.Sparkles size={18} />
                                {isSubmitting ? t('visions.creator.promoting') : t('visions.creator.promoteButton')}
                            </motion.button>
                            <p className="text-xs text-gray-400 mt-1">{t('visions.creator.promoteDescription', { cost: 5000 })}</p>
                        </div>
                        <button onClick={() => handleCreateVision(false)} disabled={isSubmitting} className="px-6 py-2 bg-purple-600 text-white font-bold rounded-full hover:bg-purple-700 transition-colors">
                            {isSubmitting ? t('visions.creator.creating') : t('visions.creator.createButton')}
                        </button>
                    </div>
                )}

                {showAiModal && (
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
                        <div className="bg-gray-900/80 p-6 rounded-xl shadow-glow max-w-lg w-full border border-purple-500/50">
                            <h2 className="text-2xl font-bold text-white font-playfair mb-4">{t('visions.creator.aiModalTitle')}</h2>
                            <textarea
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder={t('visions.creator.aiPlaceholder')}
                                className="w-full h-32 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white mb-4"
                            />
                            <div className="flex justify-end space-x-4">
                                <button onClick={() => setShowAiModal(false)} className="px-6 py-2 bg-gray-600 text-white font-bold rounded-full hover:bg-gray-700 transition-colors">
                                    {t('common.cancel')}
                                </button>
                                <button onClick={handleGenerateContent} className="px-6 py-2 bg-purple-600 text-white font-bold rounded-full hover:bg-purple-700 transition-colors">
                                    {t('visions.creator.generateButton')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {showMusicModal && <MusicModal />}
            </motion.div>
        </div>
    );
};

export default VisionCreator;
